import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { projects } from "@/projects";
import { logout } from "@/auth/adminAuth";
import ExplorerBar from "@/components/explorer/ExplorerBar";
import { clearUnlocked } from "@/lib/access";
import { useTranslation } from "react-i18next";

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const breadcrumbs = useMemo(
    () => [
      { label: t("nav.admin"), to: "/admin" },
      { label: t("nav.projects"), to: "/admin" }
    ],
    [t]
  );

  const copyLink = async (slug: string, ticket: string | undefined) => {
    if (!ticket) return;
    const url = `${window.location.origin}/p/${slug}?qr=1&t=${encodeURIComponent(ticket)}`;
    await navigator.clipboard.writeText(url);
  };

  const qrDownloadHref = (slug: string, ticket: string | undefined) => {
    const safeTicket = ticket || '';
    const url = `${window.location.origin}/p/${slug}?qr=1&t=${encodeURIComponent(safeTicket)}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=512x512&data=${encodeURIComponent(url)}`;
  };

  return (
    <MainLayout>
      <div className="pt-28 sm:pt-32 pb-20 bg-[#2A2A2A] min-h-screen">
        <div className="container-custom space-y-6">
          <ExplorerBar breadcrumbs={breadcrumbs} />

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="transition-all duration-200">
              <h1 className="text-3xl font-semibold text-[#E0E0E0]">{t('admin.dashboardTitle')}</h1>
              <p className="text-[#AAAAAA]">{t('admin.dashboardSubtitle')}</p>
            </div>
            <button
              onClick={() => {
                logout();
                navigate("/login");
              }}
              className="px-4 py-2 rounded-lg bg-[#1f1f1f] text-[#E0E0E0] border border-[#2f2f2f] hover:border-[#C8102E] transition-colors"
            >
              {t('common.logout')}
            </button>
          </div>

          <div className="overflow-x-auto bg-[#232323] border border-[#2f2f2f] rounded-xl shadow-lg shadow-black/20 transition-all duration-200">
            <table className="w-full text-left min-w-[600px]">
              <thead className="text-[#B5B5B5] text-sm">
                <tr>
                  <th className="px-4 py-3">{t('admin.table.project')}</th>
                  <th className="px-4 py-3">{t('admin.table.slug')}</th>
                  <th className="px-4 py-3">{t('admin.table.accessPin')}</th>
                  <th className="px-4 py-3">{t('admin.table.actions')}</th>
                  <th className="px-4 py-3">{t('admin.table.tickets')}</th>
                  <th className="px-4 py-3">{t('admin.table.qr')}</th>
                </tr>
              </thead>
              <tbody className="text-[#E0E0E0]">
                {projects.map((project) => (
                  <tr key={project.slug} className="border-t border-[#2f2f2f] hover:bg-[#1d1d1d]/60 transition-colors">
                    <td className="px-4 py-3">{project.info.title}</td>
                    <td className="px-4 py-3 text-sm text-[#AAAAAA]">{project.slug}</td>
                    <td className="px-4 py-3 font-mono">{project.accessPin}</td>
                    <td className="px-4 py-3 space-x-2">
                      <button
                        onClick={() => copyLink(project.slug, project.qrTickets[0])}
                        className="px-3 py-2 rounded-lg bg-[#1f1f1f] border border-[#2f2f2f] hover:border-[#C8102E] text-sm transition-colors"
                        disabled={!project.qrTickets[0]}
                      >
                        {t('admin.copyLink')}
                      </button>
                      <button
                        onClick={() => window.open(`/p/${project.slug}?qr=1&t=${encodeURIComponent(project.qrTickets[0] || '')}`, "_blank")}
                        className="px-3 py-2 rounded-lg bg-[#C8102E] text-white text-sm hover:bg-[#b00e29] transition-colors"
                        disabled={!project.qrTickets[0]}
                      >
                        {t('admin.open')}
                      </button>
                      <button
                        onClick={() => clearUnlocked(project.slug)}
                        className="px-3 py-2 rounded-lg bg-[#1f1f1f] border border-[#2f2f2f] hover:border-[#C8102E] text-sm transition-colors"
                      >
                        {t('admin.unlockLocal')}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#B5B5B5]">
                      {project.qrTickets.join(", ")}
                    </td>
                    <td className="px-4 py-3 space-x-2">
                      <a
                        href={qrDownloadHref(project.slug, project.qrTickets[0])}
                        download={`qr-${project.slug}.png`}
                        className="px-3 py-2 rounded-lg bg-[#1f1f1f] border border-[#2f2f2f] hover:border-[#C8102E] text-sm transition-colors inline-block"
                        aria-disabled={!project.qrTickets[0]}
                      >
                        {t('admin.downloadQr')}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminDashboard;
